import classNames from 'classnames'
import React, { useRef, MouseEvent, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Box, Card, LinearProgress, SxProps, Tab, Tabs, Theme, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'

import { useMobileSize } from '../../features/responsiveness/hooks/responsivenessHooks'
import { useIntersectionObserverEntry } from '../../hooks/useIntersectionObserverEntry'
import './GRTabNavigation.scss'

export type GRTab = {
  id: string
  name: ReactNode
  disabled?: boolean
  disabledTooltip?: ReactNode
}
/**
 * GRTabNavigation Tab Navigation on top of page and under app header
 */
interface GRTabNavigationProps {
  tabs: GRTab[]
  selectedTab?: number
  onChange?: (tabIndex: number) => void
  sticky?: boolean
  pagePrefix?: string
  query?: string
  hasSecondaryStickyElement?: boolean
  isLoading?: boolean
}

const observerOptions = { rootMargin: '-144px 0px 0px 0px' }

const GRTabNavigation: React.FC<GRTabNavigationProps> = ({
  tabs = [],
  selectedTab = 0,
  onChange,
  sticky = true,
  pagePrefix,
  query = '',
  hasSecondaryStickyElement = false,
  isLoading,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef(null)
  const mobileSize = useMobileSize()

  useIntersectionObserverEntry(cardRef, observerOptions, (e) => {
    if (e && e.target) {
      const { target, isIntersecting } = e
      const shouldPin = hasSecondaryStickyElement && !mobileSize
      target.classList.toggle('GRTabNavigation--pinned', shouldPin && !isIntersecting)
    }
  })

  if (!tabs.length) {
    return null
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    onChange && onChange(newValue)
    if (sticky) {
      const offsetTop = containerRef.current?.offsetTop || 0
      setTimeout(() => window.scrollTo({ top: (offsetTop || 0) - 95, behavior: 'smooth' }), 0)
    }
  }

  return (
    <>
      <Box ref={containerRef} sx={{ height: '1px', backgroundColor: (theme) => theme.palette.background.paper }}></Box>
      <Card
        ref={cardRef}
        className={classNames('GRTabNavigation', { 'GRTabNavigation--mobile': mobileSize, 'GRTabNavigation--sticky': sticky })}
        sx={{ mb: 3, position: 'relative' }}
      >
        <Tabs value={selectedTab} onChange={handleChange} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab) => (
            <Tab key={tab.id} label={tab.name} component={Link} to={`${pagePrefix}/${tab.id}${query && query}`} />
          ))}
        </Tabs>
        <TabNavigationLoadingBar isLoading={isLoading} />
      </Card>
    </>
  )
}

export default GRTabNavigation

type GRSubTabNavigationProps = {
  tabs: GRTab[]
  selectedTab?: string
  onChange?: (event: MouseEvent<HTMLElement>, newValue: string) => void
  pagePrefix?: string
  query?: string
}

export const GRSubTabNavigation: React.FC<GRSubTabNavigationProps> = ({ tabs, selectedTab, onChange, pagePrefix, query }) => {
  const itemSx: SxProps<Theme> = {
    flex: '1 1 100%',
    borderColor: (theme) => theme.palette.primary.main,
    '&.Mui-selected': {
      color: (theme) => theme.palette.primary.contrastText,
      backgroundColor: (theme) => theme.palette.primary.main,
      '&:hover': {
        backgroundColor: (theme) => theme.palette.primary.dark,
      },
    },
  }

  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue) {
      const selectedTab = tabs.find((tab) => tab.id === newValue)
      if (selectedTab && !selectedTab.disabled && onChange) {
        onChange(event, newValue)
      }
    }
  }

  return (
    <ToggleButtonGroup value={selectedTab} onChange={handleChange} exclusive size="small" sx={{ width: '100%' }} className="NavigationModeToggler">
      {tabs.map((tab) => {
        const buttonContent = (
          <ToggleButton key={tab.id} value={tab.id} disabled={tab.disabled} sx={itemSx} component={Link} to={`${pagePrefix}/${tab.id}${query ? query : ''}`}>
            {tab.name}
          </ToggleButton>
        )

        return tab.disabled && tab.disabledTooltip ? (
          <Tooltip key={tab.id} title={tab.disabledTooltip} arrow>
            <Box sx={{ display: 'flex', flex: '1 1 100%' }}>{buttonContent}</Box>
          </Tooltip>
        ) : (
          buttonContent
        )
      })}
    </ToggleButtonGroup>
  )
}

/** Thin progress line at the bottom edge of the tab bar. Uses opacity fade to avoid layout shift. */
const TabNavigationLoadingBar: React.FC<{ isLoading?: boolean }> = ({ isLoading }) => {
  return (
    <LinearProgress
      variant="indeterminate"
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 3,
        opacity: isLoading ? 1 : 0,
        transition: 'opacity 300ms ease-in-out',
        backgroundColor: 'transparent',
        '& .MuiLinearProgress-bar': {
          borderRadius: 0,
        },
      }}
    />
  )
}
